// This is a component for editing a healthcare worker, and adding a new healthcare worker
// the same modal (popup) gets used for both, but when you are editing, the fields in the popup
// have the values from the existing healthcare worker

import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    TextField,
    MenuItem,
} from '@mui/material';

const AddEditHealthcareWorkerModal = ({ open, onClose, row, onSave }) => {
    // Initialize form data
    const [formData, setFormData] = useState({
        // shouldn't be able to edit their id, or their password (at least not here)
        first_name: '',
        last_name: '',
        email: '',
        profession: '',
        clinic_name: '',
        department: '',
        institution_account_id: '',
        // country_name: '',
    });

    const [formErrors, setFormErrors] = useState({
        first_name: '',
        last_name: '',
        email: '',
        profession: '',
        clinic_name: '',
        department: '',
        institution_account_id: '',
        // country_name: '',
    });

    // Track fields that have been touched
    // since it would be annoying to give an error message before the user has finished entering text into that box
    // so we will wait until after they are done and have "touched" it
    const [touched, setTouched] = useState({});

    const [institutions, setInstitutions] = useState([]);

    // Dropdown options for specific fields
    const dropdownOptions = {
        // make sure these are the same values as the Profession enum in the backend, that's what the server requires
        profession: [
            'Medical doctor',
            'Clinician',
            'Clinical officer',
            'Physician assistant',
            'Nurse',
            'Pharmacist',
            'Other',
        ],
        // // make sure these are the same values as the countries in the country_map.py file in the backend, that's what the app uses
        country_name: [
            'Algeria',
            'Angola',
            'Argentina',
            'Bangladesh',
            'Benin',
            'Bolivia',
            'Botswana',
            'Brazil',
            'Burkina Faso',
            'Burundi',
            'Cabo Verde',
            'Cambodia',
            'Cameroon',
            'Central African Republic',
            'Chad',
            'Chile',
            'Colombia',
            'Comoros',
            'Congo',
            "Côte d'Ivoire",
            'Democratic Republic of the Congo',
            'Djibouti',
            'Ecuador',
            'Egypt',
            'Equatorial Guinea',
            'Eritrea',
            'Eswatini',
            'Ethiopia',
            'Falkland Islands',
            'French Guiana',
            'Gabon',
            'Gambia',
            'Ghana',
            'Guinea',
            'Guinea-Bissau',
            'Guyana',
            'India',
            'Indonesia',
            'Kenya',
            'Laos',
            'Lesotho',
            'Libya',
            'Madagascar',
            'Malawi',
            'Malaysia',
            'Mali',
            'Mauritania',
            'Mauritius',
            'Mongolia',
            'Morocco',
            'Mozambique',
            'Myanmar',
            'Namibia',
            'Niger',
            'Nigeria',
            'Pakistan',
            'Paraguay',
            'Peru',
            'Philippines',
            'Rwanda',
            'Sao Tome & Principe',
            'Senegal',
            'Seychelles',
            'Sierra Leone',
            'Singapore',
            'Somalia',
            'South Africa',
            'South Sudan',
            'Sri Lanka',
            'Sudan',
            'Suriname',
            'Taiwan',
            'Tanzania',
            'Thailand',
            'Togo',
            'Tunisia',
            'Uganda',
            'United States',
            'Uruguay',
            'Venezuela',
            'Vietnam',
            'Zambia',
            'Zimbabwe',
        ],
    };

    // useEffect to set form data and clear errors/touched on modal open
    useEffect(() => {
        // Function to reset form and error states
        const resetForm = () => {
            // Set form data based on whether editing (row exists) or adding
            setFormData({
                // shouldn't be able to edit their id, or their password (at least not here)
                first_name: row?.first_name || '',
                last_name: row?.last_name || '',
                email: row?.email || '',
                profession: row?.profession || '',
                clinic_name: row?.clinic_name || '',
                department: row?.department || '',
                institution_account_id: row?.institution_account_id || '',
                // country_name: row?.country_name || '',
            });

            // Clear all errors
            setFormErrors({
                first_name: '',
                last_name: '',
                email: '',
                profession: '',
                clinic_name: '',
                department: '',
                country_name: '',
            });

            // Clear all touched fields
            setTouched({});
        };

        const fetchInstitutions = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/institution-account/list`
                );
                const data = await response.json();
                setInstitutions(data);
            } catch (err) {
                console.error('Error fetching institutions', err);
            }
        };

        // Call the reset function whenever the modal opens (open prop changes)
        if (open) {
            resetForm();
            fetchInstitutions();
        }
    }, [open, row]); // Trigger when modal 'open' state or 'row' changes

    const validateFieldsDirectly = (fieldName, value) => {
        let errors = { ...formErrors };
        // TODO: these error messages are not showing up on the form, i think it should go in helperText
        const validateField = () => {
            switch (fieldName) {
                case 'first_name':
                    errors.first_name = !value ? 'First name is required' : '';
                    break;
                case 'last_name':
                    errors.last_name = !value ? 'Last name is required' : '';
                    break;
                case 'email':
                    // pretty sure this is the regex used by pydantic in the backend, so we want to use the same one to give users
                    // warnings before they try to submit.
                    // otherwise they submit and get errors about invalid emails that they didn't get warning about

                    // the line below this disables the warning on the regex for only the line below it
                    // eslint-disable-next-line no-control-regex
                    const emailRegex =
                        /^(?:[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-zA-Z0-9-]*[a-zA-Z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*)\])$/;
                    if (!value) {
                        errors.email = 'Email is required';
                    } else if (!emailRegex.test(value)) {
                        errors.email = 'Email format is invalid';
                    } else {
                        errors.email = '';
                    }
                    break;
                case 'profession':
                    errors.profession = !value ? 'Profession is required' : '';
                    break;
                case 'clinic_name':
                    errors.clinic_name = !value
                        ? 'Clinic name is required'
                        : '';
                    break;
                case 'department':
                    errors.department = !value ? 'Department is required' : '';
                    break;
                case 'country_name':
                    errors.country_name = !value ? 'Country is required' : '';
                    break;
                default:
                    break;
            }
        };

        validateField();

        setFormErrors(errors);
    };

    // Adjust handleFieldChange to validate immediately if the field has already been touched once
    const handleFieldChange = (e) => {
        const { name, value } = e.target;

        // Update the form data immediately
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        // Perform immediate validation based on the latest input value
        if (touched[name]) {
            validateFieldsDirectly(name, value);
        }
    };

    // HandleBlur ensures that validation is performed only when the user blurs the input for the first time
    const handleBlur = (e) => {
        const fieldName = e.target.name;
        const fieldValue = e.target.value; // Get the field's current value

        // Mark the field as touched after it is blurred
        setTouched((prevTouched) => ({
            ...prevTouched,
            [fieldName]: true,
        }));

        // Run validation only when the field is blurred
        validateFieldsDirectly(fieldName, fieldValue);
    };

    const handleSaveWithValidation = () => {
        // Flag to check if all fields are valid
        let allValid = true;

        // Validate each field in formData
        Object.keys(formData).forEach((fieldName) => {
            const fieldValue = formData[fieldName];
            validateFieldsDirectly(fieldName, fieldValue);

            // If there's an error, mark form as invalid
            if (formErrors[fieldName]) {
                allValid = false;
            }
        });

        // If all fields are valid, proceed with saving
        if (allValid) {
            handleSave();
        }
    };

    // Update form data on input change
    // const handleChange = (e) => {
    // 	const { name, value } = e.target;
    // 	setFormData((prev) => ({ ...prev, [name]: value }));
    // };

    // Handle saving of the updated data
    const handleSave = () => {
        onSave(formData, row?.id); // Pass updated form data to parent component
        onClose(); // Close the modal
    };

    return (
        <Dialog open={open} onClose={onClose}>
            {/* depending on if row is not null or null, change the title from editing to adding a new healthcare worker */}
            <DialogTitle>
                {row ? 'Edit Healthcare Worker' : 'Add Healthcare Worker'}
            </DialogTitle>
            <DialogContent>
                {/* Fields for editing/adding healthcare worker */}
                <TextField
                    label="First Name"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.first_name && touched.first_name}
                    helperText={formErrors.first_name && touched.first_name}
                    fullWidth
                    margin="dense"
                />
                <TextField
                    label="Last Name"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.last_name && touched.last_name}
                    helperText={formErrors.last_name && touched.last_name}
                    fullWidth
                    margin="dense"
                />

                <TextField
                    label="Email"
                    name="email"
                    value={formData.email}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.email && touched.email}
                    helperText={formErrors.email && touched.email}
                    fullWidth
                    margin="dense"
                />
                {/* a dropdown */}
                <TextField
                    // this makes it a dropdown
                    select
                    label="Profession"
                    name="profession"
                    value={formData.profession}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.profession && touched.profession}
                    helperText={formErrors.profession && touched.profession}
                    fullWidth
                    margin="dense"
                >
                    {dropdownOptions.profession.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Clinic Name"
                    name="clinic_name"
                    value={formData.clinic_name}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.clinic_name && touched.clinic_name}
                    helperText={formErrors.clinic_name && touched.clinic_name}
                    fullWidth
                    margin="dense"
                />
                <TextField
                    select
                    label="Institution"
                    name="institution_account_id"
                    value={formData.institution_account_id}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={
                        !!formErrors.institution_account_id &&
                        touched.institution_account_id
                    }
                    helperText={
                        formErrors.institution_account_id &&
                        touched.institution_account_id
                    }
                    fullWidth
                    margin="dense"
                >
                    {institutions.map((inst) => (
                        <MenuItem key={inst.id} value={inst.id}>
                            {inst.institution_name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Department"
                    name="department"
                    value={formData.department}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.department && touched.department}
                    helperText={formErrors.department && touched.department}
                    fullWidth
                    margin="dense"
                />
                {/* <TextField
                    // this makes it a dropdown
                    select
                    label="Country"
                    name="country_name"
                    value={formData.country_name}
                    onChange={handleFieldChange}
                    onBlur={handleBlur}
                    error={!!formErrors.country_name && touched.country_name}
                    helperText={formErrors.country_name && touched.country_name}
                    fullWidth
                    margin="dense"
                >
                    {dropdownOptions.country_name.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </TextField> */}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleSaveWithValidation}
                    color="primary"
                    disabled={Object.values(formErrors).some(
                        (error) => error !== ''
                    )}
                >
                    {/* depending on if row is not null or null, change the text from saving edits to adding a new healthcare worker */}
                    {row ? 'Save Changes' : 'Add Healthcare Worker'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddEditHealthcareWorkerModal;
